'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { trackAndLogError } from '@/lib/errors/errorTracker';

// ============================================================================
// Types
// ============================================================================

export interface AppErrorBoundaryProps {
  children: ReactNode;
  /** fallback UI를 직접 지정할 때 사용 */
  fallback?: ReactNode;
  /** 에러 발생 시 표시할 제목 (fallback 미지정 시) */
  title?: string;
  /** 에러 발생 시 표시할 메시지 (fallback 미지정 시) */
  message?: string;
  /** 에러 추적용 컴포넌트 이름 */
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// Component
// ============================================================================

/**
 * 섹션 단위 Error Boundary
 *
 * 개별 섹션/위젯에서 발생하는 렌더링 에러를 격리하여
 * 나머지 페이지가 정상 동작하도록 보호한다.
 *
 * @example
 * <AppErrorBoundary componentName="ChannelAnalytics" title="애널리틱스 오류">
 *   <ChannelAnalyticsSection channelId={id} />
 * </AppErrorBoundary>
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, State> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    trackAndLogError(error, {
      component: this.props.componentName ?? 'AppErrorBoundary',
      action: 'render_error',
      additionalData: {
        componentStack: errorInfo.componentStack ?? undefined,
      },
    }, 'error');
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // 커스텀 fallback이 지정된 경우
    if (this.props.fallback) {
      return this.props.fallback;
    }

    // 기본 fallback UI
    const title = this.props.title ?? '오류가 발생했습니다';
    const message =
      this.props.message ??
      '이 섹션을 불러오는 중 문제가 발생했습니다. 다시 시도해 주세요.';

    return (
      <div className="bg-white rounded-xl shadow-sm border border-red-100 p-6 mb-6">
        <div className="flex flex-col items-center text-center py-4 space-y-3">
          <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-neutral-800">{title}</p>
            <p className="text-xs text-neutral-500 mt-1">{message}</p>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <p className="text-xs font-mono text-red-600 bg-red-50 rounded px-3 py-1.5 max-w-full break-all">
              {this.state.error.message}
            </p>
          )}
          <button
            onClick={this.handleReset}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-3 h-3" />
            다시 시도
          </button>
        </div>
      </div>
    );
  }
}
